import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import _compact from 'lodash/compact';
import { ReactComponent as HomeIcon } from '@lifesize/ux-assets/vectors/svgIcons/home.svg';
import { ReactComponent as CalendarIcon } from '@lifesize/ux-assets/vectors/svgIcons/calendar.svg';
import { ReactComponent as ChatIcon } from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import { ReactComponent as PersonIcon } from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import { ReactComponent as RoomSystemsIcon } from '@lifesize/ux-assets/vectors/svgIcons/room-system.svg';
import { ReactComponent as PeopleIcon } from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import { ReactComponent as LsLogoHorizontal } from '@lifesize/ux-assets/vectors/svgLogos/lsHorizontalTM.svg';
import { ReactComponent as LsLogoBuddy } from '@lifesize/ux-assets/vectors/svgLogos/lsBuddyTM.svg';

import classes from './SideNav.module.scss';
import SideNavItem from './SideNavItem.js';
import { hooks } from '@lifesize/clients.sdk';
import { routes } from 'constants/Application';

class SideNav extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dragging: false,
      startingX: null
    };
  }

  handleDoubleClick = (e) => {
    e.preventDefault();
    this.props.toggleSideNav();
  };

  handleMouseDown = (e) => {
    e.preventDefault();
    this.setState({
      dragging: true,
      startingX: e.screenX
    });
    document.addEventListener('mousemove', this.handleMouseMove, false);
    document.addEventListener('mouseup', this.handleMouseUp, false);
  };

  handleMouseUp = (e) => {
    e.preventDefault();

    // snap back to narrow or wide side nav
    this.endDrag();
  };

  handleMouseMove = (e) => {
    e.preventDefault();

    let change;
    if (this.props.isSideNavWide) {
      if (e.screenX > this.state.startingX) return;

      change = this.state.startingX - e.screenX;
    } else {
      if (e.screenX < this.state.startingX) return;

      change = e.screenX - this.state.startingX;
    }

    // Snap to narrow or wide side nav
    if (change > this.props.snapThreshold) {
      this.props.toggleSideNav();
      this.endDrag();
    }
  };

  endDrag() {
    this.setState({
      dragging: false,
      startingX: null
    });
    document.removeEventListener('mousemove', this.handleMouseMove, false);
    document.removeEventListener('mouseup', this.handleMouseUp, false);
  }

  render() {
    const {
      hideContacts,
      hideMeetings,
      hideRooms,
      isCallConnected,
      isChatEnabled,
      isSideNavWide,
      markCallHistoryAsViewed,
      missedCalls,
      routeLoaded,
      unreadChats,
      upcomingMeetings
    } = this.props;

    const wide = isSideNavWide || false;

    return (
      <div className={clsx([classes.container, isSideNavWide ? classes.wide : ''])}>
        <nav id="nav" className={classes.nav}>
          <ul>
            <SideNavItem
              badgeCount={missedCalls}
              icon={<HomeIcon />}
              label={<FormattedMessage id="components.sidenav.home" description="Home label" defaultMessage="Home" />}
              markCallHistoryAsViewed={markCallHistoryAsViewed}
              route={routes.base}
              routeLoaded={routeLoaded}
              wide={wide}
            />
            {
              <SideNavItem
                badgeCount={upcomingMeetings}
                icon={<CalendarIcon />}
                label={
                  <FormattedMessage
                    id="components.sidenav.upcoming"
                    description="Upcoming label"
                    defaultMessage="Upcoming"
                  />
                }
                route={routes.upcoming}
                routeLoaded={routeLoaded}
                wide={wide}
              />
            }
            <SideNavItem
              badgeCount={unreadChats}
              icon={<ChatIcon />}
              isChatEnabled={isChatEnabled}
              label={<FormattedMessage id="components.sidenav.chat" description="Chat label" defaultMessage="Chat" />}
              route={routes.chat}
              routeLoaded={routeLoaded}
              wide={wide}
            />
            {!isCallConnected && !hideContacts ? (
              <SideNavItem
                icon={<PersonIcon />}
                label={
                  <FormattedMessage
                    id="components.sidenav.contacts"
                    description="Contacts label"
                    defaultMessage="Contacts"
                  />
                }
                route={routes.contacts}
                routeLoaded={routeLoaded}
                wide={wide}
              />
            ) : null}
            {!isCallConnected && !hideRooms ? (
              <SideNavItem
                icon={<RoomSystemsIcon />}
                label={
                  <FormattedMessage
                    id="components.sidenav.roomSystems"
                    description="Room Systems label"
                    defaultMessage="Room Systems"
                  />
                }
                route={routes.roomSystems}
                routeLoaded={routeLoaded}
                wide={wide}
              />
            ) : null}
            {!isCallConnected && !hideMeetings ? (
              <SideNavItem
                icon={<PeopleIcon />}
                label={
                  <FormattedMessage
                    id="components.sidenav.meetings"
                    description="Meetings label"
                    defaultMessage="Meetings"
                  />
                }
                route={routes.meetings}
                routeLoaded={routeLoaded}
                wide={wide}
              />
            ) : null}
          </ul>
        </nav>
        <div className={classes.logoContainer}>
          <div className={classes.logoDisplay}>
            <LsLogoHorizontal className={_compact([classes.logo, classes.logoHorizontal]).join(' ')} />
            <LsLogoBuddy className={clsx([classes.logo, classes.logoBuddy])} />
          </div>
        </div>
        {!isCallConnected ? (
          <div
            id="sideNavHandle"
            draggable
            className={classes.navHandle}
            onMouseDown={this.handleMouseDown}
            onDoubleClick={this.handleDoubleClick}
          />
        ) : null}
      </div>
    );
  }
}

function SideNavNew(props) {
  const callConnected = hooks.useCallConnected();
  const isSideNavWide = props.isSideNavWide || (callConnected && props.isInCallVideoMinimized);
  return <SideNav {...props} isCallConnected={callConnected} isSideNavWide={isSideNavWide} />;
}

SideNav.propTypes = {
  hideContacts: PT.bool,
  hideMeetings: PT.bool,
  hideRooms: PT.bool,
  isCallConnected: PT.bool,
  isChatEnabled: PT.bool,
  isSideNavWide: PT.bool.isRequired,
  markCallHistoryAsViewed: PT.func.isRequired,
  missedCalls: PT.number.isRequired,
  routeLoaded: PT.string,
  snapThreshold: PT.number.isRequired,
  toggleSideNav: PT.func.isRequired,
  unreadChats: PT.number.isRequired,
  upcomingMeetings: PT.number
};

export { SideNav };
export default SideNavNew;
